import { useState, useCallback, useEffect } from "react";

type VideoInfo = {
  id: string;
  componentId: string;
  index: number;
};

// Global state to manage video playback across the entire application
let globalCurrentVideo: VideoInfo | null = null;
const globalListeners: Set<(video: VideoInfo | null) => void> = new Set();

const notifyListeners = (video: VideoInfo | null) => {
  globalListeners.forEach((listener) => listener(video));
};

export const useVideoManager = (componentId: string) => {
  const [currentVideo, setCurrentVideo] = useState<VideoInfo | null>(globalCurrentVideo);

  // Subscribe to global video state changes
  useEffect(() => {
    const listener = (video: VideoInfo | null) => {
      setCurrentVideo(video);
    };

    globalListeners.add(listener);

    return () => {
      globalListeners.delete(listener);
    };
  }, []);

  const playVideo = useCallback(
    (videoId: string, index: number) => {
      const newVideo: VideoInfo = {
        id: videoId,
        componentId,
        index,
      };

      // If the same video is clicked, pause it
      if (globalCurrentVideo?.id === videoId && globalCurrentVideo?.componentId === componentId && globalCurrentVideo?.index === index) {
        globalCurrentVideo = null;
        notifyListeners(null);
        return;
      }

      // Set the new video as active - this will automatically cause all other videos to pause
      // due to the isVideoPlaying check in each component
      globalCurrentVideo = newVideo;
      notifyListeners(newVideo);
    },
    [componentId]
  );

  const pauseVideo = useCallback(
    (videoId?: string, index?: number) => {
      // Only pause if this video is actually the currently playing video
      // This prevents other videos from accidentally pausing the active video
      if (videoId && index !== undefined) {
        const isCurrentlyPlaying =
          globalCurrentVideo?.id === videoId && globalCurrentVideo?.componentId === componentId && globalCurrentVideo?.index === index;
        if (isCurrentlyPlaying) {
          globalCurrentVideo = null;
          notifyListeners(null);
        }
      } else {
        // If no specific video is provided, pause everything (for manual calls)
        globalCurrentVideo = null;
        notifyListeners(null);
      }
    },
    [componentId]
  );

  const isVideoPlaying = useCallback(
    (videoId: string, index: number) => {
      return globalCurrentVideo?.id === videoId && globalCurrentVideo?.componentId === componentId && globalCurrentVideo?.index === index;
    },
    [componentId]
  );

  // Keep the pauseAllVideos function for manual use if needed
  const pauseAllVideos = useCallback(() => {
    globalCurrentVideo = null;
    notifyListeners(null);
  }, []);

  return {
    currentVideo,
    playVideo,
    pauseVideo,
    isVideoPlaying,
    pauseAllVideos,
  };
};
